import { useCallback, useMemo } from 'react'

import { useFacilityCSVProcessor } from '../hooks/useFacilityCSVProcessor'
import { getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { UseFormMethods } from 'react-hook-form'
import { HiOutlineDownload } from 'react-icons/hi'

import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'

import {
  Button,
  FormFileUploadControl,
  StyledFormInputControl,
  Table,
  TableBody,
  TableHeader,
} from '@/components/ui'
import { isValidFileType } from '@/features/device-crud'

import { FormInputsIProps } from '../types/types'
import { CSVParseResultI, downloadCSVTemplate } from '../utils/csvParser'
import { getTimezoneFromCoordinates } from '../utils/geocoding'
import { createColumns } from '../utils/tableColumns'

interface BulkUploadFacilitiesFormIProps {
  form: UseFormMethods<FormInputsIProps>
  parseResult: CSVParseResultI | null
  onParseResultChange: (result: CSVParseResultI | null) => void
}

export const BulkUploadFacilitiesForm = ({
  form,
  parseResult,
  onParseResultChange,
}: BulkUploadFacilitiesFormIProps) => {
  const { register, watch, trigger } = form
  const fileUpload = watch('file')

  // Use custom hook to handle CSV processing, geocoding, and timezone lookup
  const { isLoading, isGeocoding, geocodingProgress } = useFacilityCSVProcessor(
    fileUpload,
    onParseResultChange
  )

  // Handle address updates from AddressSearchInput
  const handleUpdateAddress = useCallback(
    (
      rowIndex: number,
      address: { value: string; latitude?: number; longitude?: number }
    ) => {
      if (!parseResult) return

      const updatedData = [...parseResult.data]
      const facility = updatedData[rowIndex]

      // Update address and coordinates
      facility.address = address.value
      facility.lat = address.latitude?.toString() || ''
      facility.lon = address.longitude?.toString() || ''

      // Update timezone based on coordinates
      if (address.latitude && address.longitude) {
        const timezone = getTimezoneFromCoordinates(
          address.latitude.toString(),
          address.longitude.toString()
        )
        if (timezone) {
          facility.timezone = timezone
        }
      }

      // Clear any geocoding errors since we have new valid data
      facility.geocodingError = undefined

      onParseResultChange({
        ...parseResult,
        data: updatedData,
      })
    },
    [parseResult, onParseResultChange]
  )

  const columns = useMemo(
    () => createColumns({ onUpdateAddress: handleUpdateAddress }),
    [handleUpdateAddress]
  )

  const tableData = useMemo(() => {
    return parseResult?.data || []
  }, [parseResult])

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <Box px='6'>
      <Box mb={3}>
        <Button
          leftIcon={<HiOutlineDownload size='20px' />}
          name='asd'
          onClick={downloadCSVTemplate}
          size='sm'
          variant='secondary'
        >
          Download CSV Template
        </Button>
        <Text color='gray.600' fontSize='12px' mt={2}>
          Download the template to see the required format. Latitude, longitude,
          and timezone can be left empty - coordinates and timezone will be
          automatically determined from the address.
        </Text>
      </Box>

      <StyledFormInputControl mb={3}>
        <Tooltip
          isDisabled={isValidFileType(fileUpload)}
          label='Supported file types include CSV'
          shouldWrapChildren
        >
          <FormFileUploadControl
            accept='.csv'
            dataTestId='createFacility_modal_fileUpload'
            errorMessage={
              fileUpload &&
              !isValidFileType(fileUpload) &&
              'File should be in .csv format'
            }
            id='file'
            inputRef={register({ required: true })}
            isInvalid={!isValidFileType(fileUpload)}
            label='Facilities'
            onFileRemoved={() => {
              trigger(['file'])
              onParseResultChange(null)
            }}
            placeholder='Attach CSV'
          />
        </Tooltip>
      </StyledFormInputControl>

      {/* Loading indicator */}
      {isLoading && (
        <Box mb={3}>
          <Text color='gray.600' fontSize='sm'>
            Parsing CSV file...
          </Text>
        </Box>
      )}

      {/* Geocoding progress indicator */}
      {isGeocoding && (
        <Box mb={3}>
          <Text color='gray.600' fontSize='sm' fontWeight='medium'>
            Geocoding addresses... {geocodingProgress.current} of{' '}
            {geocodingProgress.total}
          </Text>
          <Text color='gray.500' fontSize='xs' mt={1}>
            Converting addresses to coordinates. This may take a moment.
          </Text>
        </Box>
      )}

      {/* Display parsing errors */}
      {parseResult && parseResult.errors.length > 0 && (
        <Alert mb={3} status='error'>
          <AlertIcon />
          <VStack align='start' spacing={1}>
            <AlertTitle>CSV Parsing Errors</AlertTitle>
            <AlertDescription>
              {parseResult.errors.map((error, index) => (
                <Text fontSize='sm' key={index}>
                  • {error}
                </Text>
              ))}
            </AlertDescription>
          </VStack>
        </Alert>
      )}

      {/* Display Table with pasred facility data */}
      {parseResult && parseResult.data.length > 0 && (
        <Box mb={3}>
          <Table
            tableContainerStyles={{
              overflowY: 'auto',
            }}
          >
            <TableHeader table={table} />
            <TableBody rowModel={table.getRowModel()} />
          </Table>
        </Box>
      )}
    </Box>
  )
}
